import { action, computed } from "mobx";
import { v4 as uuid } from "uuid";

import http from "@app/lib/http";
import notify from "@app/components/notify";
import FilterState from "@app/components/filter/state";
import DictionaryEntry from "@app/state/model/dictionary/entry";
import { events } from "@app/lib/store";

import BaseStore from "../../base";
import report from "../../report";
import reportDocumentStore from "../../report-document/report-documents";

const STGR_LOAD = uuid();

/**
 * State management controlling the list of users in
 * the user management sections
 */
export class Dictionary extends BaseStore {
    filter = null;

    /**
     * Observable store data
     */
    observable() {
        return {
            list: [],
            loading: false,
            saving: false,
        };
    }

    constructor() {
        super();

        // handle dictionary events
        events.on("dictionary.update", () => this.load());
        events.on("dictionary.delete", () => this.load());
        events.on("dictionary.updateTemplateEntries", ({ documentId }) =>
            this.loadTemplateEntries({ documentId }),
        );
        this.filter = new FilterState({
            default: { search: "" },
        });

        this.filter.on("find", () => {
            this.load();
        });

        events.on("project.unload", () => {
            this.reset();
        });
    }

    /**
     * Return the project id if the currently loaded project
     */
    @computed get project() {
        return report.id;
    }

    /**
     * Load the list of dictionary entries from the backend
     */
    @action
    async load() {
        this.loading = true;

        let filter = this.filter.value();
        let { data } = await http
            .get(`/project/${this.project}/dictionary`, filter)
            .stagger(STGR_LOAD);

        this.list = data.list.map((entry) => {
            return new DictionaryEntry(entry);
        });

        this.loading = false;
    }

    /**
     * Load the list of template dictionary entries from the backend
     */
    @action
    async loadTemplateEntries({ documentId }) {
        if (!documentId) {
            return;
        }

        this.loading = true;
        const { clientId } = reportDocumentStore;

        let { data } = await http
            .get(`/client/${clientId}/template/${documentId}/dictionary`)
            .stagger(STGR_LOAD);

        this.list = data.list.map((entry) => {
            return new DictionaryEntry(entry);
        });

        this.loading = false;
        this.loaded = true;
    }

    /**
     * Update an entry
     */
    @action
    async save(params) {
        if (!params || !this.project || this.saving) {
            return;
        }

        try {
            let result;

            this.saving = true;

            if (params._id) {
                result = await http.put(
                    `/project/${this.project}/dictionary/${params._id}`,
                    params,
                );
            } else {
                result = await http.post(`/project/${this.project}/dictionary`, params);
            }

            // emit the update event
            const entry = new DictionaryEntry(result.data);
            events.emit("dictionary.update", entry);
        } catch (ex) {
            notify.error(ex.response?.data?.error);
            return false;
        } finally {
            this.saving = false;
        }
    }

    @action
    async saveEntryForTemplate(documentId, params) {
        const { clientId } = reportDocumentStore;

        if (!params || !documentId || this.saving) {
            return;
        }

        try {
            let result;

            this.saving = true;

            if (params._id) {
                result = await http.put(
                    `/client/${clientId}/template/${documentId}/dictionary/${params._id}`,
                    params,
                );
            } else {
                result = await http.post(
                    `/client/${clientId}/template/${documentId}/dictionary`,
                    params,
                );
            }

            // emit the update event
            const entry = new DictionaryEntry(result.data);
            events.emit("dictionary.updateTemplateEntries", {
                entry,
                templateMode: true,
                documentId,
            });
        } catch (ex) {
            notify.error(ex.response?.data?.error);
            return false;
        } finally {
            this.saving = false;
        }
    }

    /**
     * Remove an entry
     */
    @action
    async remove(id) {
        this.loading = true;
        let { data } = await http.delete(`/project/${this.project}/dictionary/${id}`);

        // emit the delete event
        let client = new DictionaryEntry(data);
        events.emit("dictionary.delete", client);

        this.loading = false;
    }

    // validate save entry
    @action
    validateSave = (entry) => {
        const found = this.list.find((i) => i.name === entry.name && i._id !== entry._id);

        if (found) {
            notify.error("Dictionary name already exists!");
            return false;
        }

        return true;
    };

    @action
    unload() {
        this.list = [];
    }
}

export default new Dictionary();
